import { useState } from "react";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/pages/ContentPage";
import { useSite } from "@/context/Providers";
import { http } from "@/lib/api";
import { breadcrumbLd, businessLd } from "@/lib/seo";

const Field = ({ id, label, ...rest }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-sm font-semibold text-gray-200">{label}</label>
    {rest.rows ? <textarea id={id} name={id} {...rest} className="w-full rounded-2xl border border-line bg-ink px-4 py-3 text-base text-white focus:border-gold focus:outline-none" data-testid={`contact-${id}-input`} />
      : <input id={id} name={id} {...rest} className="h-12 w-full rounded-2xl border border-line bg-ink px-4 text-base text-white focus:border-gold focus:outline-none" data-testid={`contact-${id}-input`} />}
  </div>
);

export default function Contact() {
  const s = useSite();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Contact", path: "/contact/" }];

  const submit = async (e) => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.currentTarget));
    setBusy(true);
    try {
      await http.post("/contact", f);
      setSent(true);
      e.target.reset();
    } catch {
      toast.error("Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Contact SHADC Dispensary | 2906 Georgia Ave NW, Washington, DC"
        description="Contact SHADC Dispensary at 2906 Georgia Ave NW, Washington, DC. Call (202) 271-6863. Open Monday–Saturday 8 AM–8 PM."
        path="/contact/" jsonLd={[breadcrumbLd(crumbs), businessLd(s)]} />
      <PageHeader crumbs={crumbs} eyebrow="Get in touch" h1="Contact SHADC" intro="Questions about an order, delivery or a product? Call, email or send us a message." />
      <div className="container-x grid gap-10 py-14 lg:grid-cols-[1fr_1.3fr]">
        {s && (
          <address className="space-y-4 not-italic" data-testid="contact-nap">
            {[[Phone, "Phone", <a key="p" href={`tel:${s.phone}`} className="hover:text-gold">{s.phone_display}</a>],
              [Mail, "Email", <a key="e" href={`mailto:${s.email}`} className="break-all hover:text-gold">{s.email}</a>],
              [MapPin, "Address", `${s.address.street}, ${s.address.city}, ${s.address.region} ${s.address.postal}`],
              [Clock, "Hours", s.hours.map((h) => `${h.days}: ${h.label}`).join(" · ")]].map(([I, t, v]) => (
              <div key={t} className="card-surface flex gap-4 p-5"><I className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden /><div><p className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">{t}</p><p className="mt-1 text-base text-white">{v}</p></div></div>
            ))}
          </address>
        )}
        <div className="card-surface p-6 sm:p-8">
          <h2 className="text-2xl font-semibold text-white">Send a message</h2>
          {sent ? <p className="mt-6 rounded-2xl border border-jade/40 bg-jade/10 p-5 text-jade-soft" role="status" data-testid="contact-success">Thanks — your message was sent. We'll get back to you soon.</p> : (
            <form onSubmit={submit} className="mt-6 grid gap-5" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="name" label="Your name" required maxLength={120} autoComplete="name" />
                <Field id="email" label="Email" type="email" required autoComplete="email" />
              </div>
              <Field id="phone" label="Phone (optional)" type="tel" autoComplete="tel" maxLength={40} />
              <Field id="message" label="Message" rows={5} maxLength={4000} />
              <button type="submit" disabled={busy} className="btn-gold" data-testid="contact-submit-button">{busy ? "Sending…" : "Send message"}</button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
